import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDateRel } from "../../lib/api";
import MobileHeader from "../../components/MobileHeader";
import { Check, AlertCircle, AlertTriangle, Info, Trash2 } from "lucide-react";
import { useI18n } from "../../lib/i18n";

const ICONS = { danger: AlertCircle, warning: AlertTriangle, info: Info, success: Check };
const COLORS = {
    danger: "bg-rose-50 text-rose-600 border-rose-200",
    warning: "bg-amber-50 text-amber-600 border-amber-200",
    info: "bg-blue-50 text-blue-600 border-blue-200",
    success: "bg-emerald-50 text-emerald-600 border-emerald-200",
};

export default function Notifications() {
    const { t } = useI18n();
    const nav = useNavigate();
    const [items, setItems] = useState([]);

    const refresh = () =>
        api.get("/notifications").then((r) => setItems(r.data.items || []));
    useEffect(() => { refresh(); }, []);

    const markAll = async () => {
        await api.post("/notifications/mark-all-read");
        refresh();
    };

    const remove = async (id, e) => {
        e.preventDefault();
        e.stopPropagation();
        await api.delete(`/notifications/${id}`);
        refresh();
    };

    return (
        <div data-testid="notifications-page">
            <MobileHeader title={t("common.notifications")} backTo={-1} variant="purple">
                {items.some((i) => !i.read) && (
                    <button
                        onClick={markAll}
                        className="mt-4 inline-block bg-white/15 backdrop-blur rounded-lg px-3 py-1.5 text-xs font-bold text-white"
                        data-testid="page-mark-all"
                    >
                        {t("common.mark_all_read")}
                    </button>
                )}
            </MobileHeader>
            <div className="px-5 -mt-4 pb-4 space-y-2">
                {items.length === 0 && (
                    <div className="bg-white rounded-2xl p-12 text-center text-sm text-slate-500 border border-slate-200">
                        <Check className="size-12 mx-auto text-emerald-400 mb-2" />
                        {t("common.no_notifications")}
                    </div>
                )}
                {items.map((n) => {
                    const Icon = ICONS[n.severity] || Info;
                    const cls = COLORS[n.severity] || COLORS.info;
                    return (
                        <button
                            key={n.id}
                            onClick={() => {
                                api.post(`/notifications/${n.id}/read`);
                                if (n.action_url) nav(n.action_url);
                            }}
                            data-testid={`notif-row-${n.id}`}
                            className={`w-full text-left bg-white rounded-2xl p-4 border border-slate-200/60 product-shadow tap-feedback ${!n.read ? "ring-1 ring-blue-100" : ""}`}
                        >
                            <div className="flex items-start gap-3">
                                <div className={`size-10 rounded-xl flex items-center justify-center border ${cls} flex-shrink-0`}>
                                    <Icon size={18} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2">
                                        <span className="font-semibold text-sm text-[#0A1930]">{n.title}</span>
                                        {!n.read && <span className="size-2 rounded-full bg-[#0066FF] mt-1.5 flex-shrink-0" />}
                                    </div>
                                    <p className="text-xs text-slate-600 mt-0.5">{n.body}</p>
                                    <p className="text-[10px] text-slate-400 mt-1">{fmtDateRel(n.created_at)}</p>
                                </div>
                                <button
                                    onClick={(e) => remove(n.id, e)}
                                    data-testid={`notif-delete-${n.id}`}
                                    className="size-8 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-500 flex items-center justify-center transition"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
